import {
  CertificateHtmlRenderOptions,
  CertificateRenderData,
  CertificateThemeId,
  renderCertificateHtml,
} from '@slocoach/certificate-templates';
import {
  CertificateTemplate,
  CertificateValidityUnit,
  ICertificate,
  ICertificateSignatory,
} from '../../../../interface';
import { getCertificateTemplateMeta } from './template-catalog';

export interface CertificateRenderRequest {
  themeId: CertificateThemeId;
  data: CertificateRenderData;
  options: CertificateHtmlRenderOptions;
}

interface BuildCertificateRenderRequestParams {
  certificate: ICertificate;
  enterprise?: {
    entLogo?: string | null;
    logo?: string | null;
    name?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    fontFamily?: string | null;
    bodyFontFamily?: string | null;
    bodyFontUrl?: string | null;
    headerFontFamily?: string | null;
    headerFontUrl?: string | null;
  } | null;
  sessionTitle?: string;
  moduleTitles?: string[];
}

const DEFAULT_MODULE_TITLES = [
  'Introduction',
  'Foundations and Key Concepts',
  'Practical Applications',
  'Case Review',
  'Assessment and Reflection',
  'Completion Summary',
];
const DEFAULT_RECIPIENT_NAME = 'Sample Learner Name';
const DEFAULT_SESSION_TITLE = 'Session Title';
const DEFAULT_SIGNATORY = {
  name: 'Program Director',
  title: 'Authorized Signatory',
  imageSrc: '',
};

export const getDefaultCertificateValues = (
  certificate?: ICertificate | null,
): ICertificate => ({
  title: certificate?.title ?? '',
  description: certificate?.description ?? '',
  template: certificate?.template ?? CertificateTemplate.CERT_TEMPLATE_1,
  header: certificate?.header ?? '',
  logos: certificate?.logos ?? [],
  badge: certificate?.badge,
  signatories: certificate?.signatories ?? [],
  validity: certificate?.validity,
});

export const sanitizeCertificate = (certificate: ICertificate): ICertificate => ({
  ...certificate,
  logos: certificate.logos && certificate.logos.length > 0 ? certificate.logos : undefined,
  badge: certificate.badge && (certificate.badge.title || certificate.badge.imageUrl)
    ? certificate.badge
    : undefined,
  signatories: certificate.signatories && certificate.signatories.length > 0
    ? certificate.signatories
    : undefined,
  validity: certificate.validity && certificate.validity.value
    ? certificate.validity
    : undefined,
});

export const getCertificateSummary = (certificate?: ICertificate | null) => {
  if (!certificate) {
    return '';
  }

  const templateMeta = getCertificateTemplateMeta(certificate.template);

  const summaryItems = [
    `Template: ${templateMeta.label}`,
    certificate.logos?.length ? `${certificate.logos.length} logo(s)` : '',
    certificate.signatories?.length ? `${certificate.signatories.length} signatory(s)` : '',
    certificate.validity
      ? `Validity: ${certificate.validity.value} ${certificate.validity.unit.toLowerCase()}(s)`
      : '',
  ].filter(Boolean);

  return summaryItems.join(' | ');
};

export const mapTemplateToThemeId = (template?: CertificateTemplate): CertificateThemeId => {
  switch (template) {
    case CertificateTemplate.CERT_TEMPLATE_2:
      return 'template2';
    case CertificateTemplate.CERT_TEMPLATE_3:
      return 'template3';
    case CertificateTemplate.CERT_TEMPLATE_1:
    default:
      return 'template1';
  }
};

const splitCertificateHeader = (header?: string) => {
  const words = (header || 'CERTIFICATE OF COMPLETION').trim().split(/\s+/);
  return {
    title: words[0] ?? 'CERTIFICATE',
    subtitle: words.slice(1).join(' ') || '',
  };
};

const getPreviewValidity = (certificate: ICertificate) => {
  if (!certificate.validity?.value || !certificate.validity?.unit) {
    return 'Validity not configured';
  }

  const unit = certificate.validity.unit === CertificateValidityUnit.YEAR ? 'Year' : 'Month';
  const suffix = certificate.validity.value > 1 ? 's' : '';

  return `${certificate.validity.value} ${unit}${suffix}`;
};

const getPreviewModules = (moduleTitles?: string[]) => {
  const items = (moduleTitles ?? []).filter(Boolean);
  return items.length > 0 ? items : DEFAULT_MODULE_TITLES;
};

const getVisibleSignatories = (signatories?: ICertificateSignatory[]) => {
  const items = (signatories ?? [])
    .filter((item) => item.name || item.designation || item.signatureImageUrl)
    .slice(0, 4)
    .map((item) => ({
      name: item.name || 'Authorized Signatory',
      title: item.designation || '',
      imageSrc: item.signatureImageUrl || '',
    }));

  return items.length > 0 ? items : [DEFAULT_SIGNATORY];
};

export const getCertificatePreviewFrameHeight = (_template?: CertificateTemplate) => 1720;

export const buildCertificateRenderRequest = ({
  certificate,
  enterprise,
  sessionTitle = DEFAULT_SESSION_TITLE,
  moduleTitles = [],
}: BuildCertificateRenderRequestParams): CertificateRenderRequest => {
  const previewModules = getPreviewModules(moduleTitles);
  const signatories = getVisibleSignatories(certificate.signatories);
  const certificateTitle = certificate.title || 'Certificate of Completion';
  const certificateDescription = certificate.description
    || 'Issued in recognition of successful completion of the selected learning experience.';
  const previewValidity = getPreviewValidity(certificate);
  const previewDate = new Date().toISOString();
  const tenantLogoUrl = enterprise?.entLogo || enterprise?.logo || undefined;
  const tenantLogoTitle = enterprise?.name || 'Tenant logo';
  const supportingLogos = (certificate.logos ?? [])
    .filter((logo) => logo.logoUrl && logo.logoUrl !== tenantLogoUrl)
    .slice(0, 5)
    .map((logo) => ({
      name: logo.title || 'Supporting logo',
      src: logo.logoUrl,
      alt: logo.title || 'Supporting logo',
    }));
  const logos = [
    ...(tenantLogoUrl
      ? [{ name: tenantLogoTitle, src: tenantLogoUrl, alt: tenantLogoTitle }]
      : []),
    ...supportingLogos,
  ];

  let badge;
  if (certificate.badge?.imageUrl) {
    badge = {
      imageSrc: certificate.badge.imageUrl,
      alt: certificate.badge.title || 'Certificate badge',
    };
  } else if (certificate.badge?.title) {
    badge = { label: certificate.badge.title };
  }

  return {
    themeId: mapTemplateToThemeId(certificate.template),
    data: {
      header: {
        eyebrow: '',
        ...splitCertificateHeader(certificate.header),
      },
      recipient: {
        name: DEFAULT_RECIPIENT_NAME,
        subtitle: 'This certifies that',
      },
      course: {
        title: sessionTitle,
        description: certificateDescription,
        modules: previewModules.map((title) => ({ title })),
      },
      logos,
      signatories,
      footer: {
        completionDate: previewDate,
        validityLabel: previewValidity,
      },
      verification: {
        showQrCode: false,
        verificationUrl: 'https://verify.example.com/certificate/CERT-2026-000123',
        verificationLabel: 'Preview verification URL:',
      },
      badge,
      tenantTheme: {
        primaryColor: enterprise?.primaryColor || undefined,
        secondaryColor: enterprise?.secondaryColor || undefined,
        fontFamily: enterprise?.fontFamily || undefined,
        bodyFontFamily: enterprise?.bodyFontFamily || undefined,
        bodyFontUrl: enterprise?.bodyFontUrl || undefined,
        headerFontFamily: enterprise?.headerFontFamily || undefined,
        headerFontUrl: enterprise?.headerFontUrl || undefined,
      },
    },
    options: {
      documentTitle: `${certificateTitle} Preview`,
    },
  };
};

export const renderCertificatePreviewHtml = (request: CertificateRenderRequest) => (
  renderCertificateHtml(request.themeId, request.data, request.options).html
);

export const renderCertificatePreviewHtmlViaService = async (
  request: CertificateRenderRequest,
): Promise<string> => renderCertificatePreviewHtml(request);
